import { Form, Formik } from 'formik';
import ModalComponent from '../../../components/modal/modal';
import Search from '../../../components/search/search';
import { useEffect, useRef, useState } from 'react';
import ModalBody from '../../../components/modal/modalBodyComponent';
import {
  CheckBoxFlex,
  FieldLevelPermissionsGroups,
  ListWrapper,
} from '../system-roles/roles.styled';
import { PermissionConfirmScrollWrapper } from './manageFeatureSetModal.styled';
import Tag from '../../../components/lozenge/tags';
import Heading from '../../../components/typography';
import {
  ROLES_PERMISSION_LIST,
  SORTED_FIELD_PERMISSION,
  SORTED_MODULE_PERMISSION,
} from '../../../constants/common';
import { handleFilterPermissionList, handleSortPermissionNames } from '../../../utils/global';
import { getFilteredPermissionData } from '../../../utils';
import {
  convertDualPermissions,
  convertPermissions,
} from '../../../utils/permission/permissionAdapter';
import {
  useAdvanceBoolean,
  useGetFeaturesPermissionsDistributable,
  usePostUpsertPermission,
} from '../../../hooks';
import { useSelector } from 'react-redux';
import { selectAuthRoutesList } from '../../configuration/routeSlice';
import { HeadingMain } from '../../../constants/style/common.styled';
import CustomCheckBox from '../../../components/radio-button/customCheckBox';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import { COLORS } from '../../../constants/style/color';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import AlertPopup from '../../../components/alert-popup';
import useGetFeaturePermissionSet from '../../../hooks/get/useGetFeaturePermissionSet';

interface ManageFeatureSetModalTypes {
  isModalOpen: boolean;
  closeModal: () => void;
  id?: string;
  checkedValues: string[];
  setCheckedValues: (values: string[]) => void;
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  permissionType?: string;
  selectedRowData: any;
}

const ManageFeatureSetModal = ({
  isModalOpen,
  closeModal,
  id,
  checkedValues,
  setCheckedValues,
  handleRequest,
  permissionType,
  selectedRowData,
}: ManageFeatureSetModalTypes): JSX.Element => {
  const userAuthList = useSelector(selectAuthRoutesList);
  const allFieldValues = userAuthList.localization?.values?.Wealthlane;
  const [manualSearch, setManualSearch] = useState('');
  const successModalDataRef = useRef<{
    payload: any;
    selectedPermissionNames: string[];
    selectedPermissionRows: {
      displayName: string;
      permissions: { text: string; allowed: boolean }[];
    }[];
  } | null>(null);
  const {
    value: isSuccessModalOpen,
    setFalse: closeSuccessModal,
    setTrue: openSuccessModal,
    customValue: successCustomValue,
    setCustomValue: setSuccessCustomValue,
  } = useAdvanceBoolean(false);

  const { mutate: upsertPermission, isLoading } = usePostUpsertPermission(
    handleRequest,
    closeModal,
    setCheckedValues
  );
  const { data: newPermissions } = useGetFeaturePermissionSet(handleRequest);
  const { data: distributablePermissions } = useGetFeaturesPermissionsDistributable(handleRequest);

  const [mergeDate, setMergeData] = useState<any>();

  useEffect(() => {
    if (distributablePermissions?.fieldLevelPermissions) {
      setMergeData(
        distributablePermissions?.permissions?.concat(
          distributablePermissions?.fieldLevelPermissions
        )
      );
    }
  }, [distributablePermissions]);

  const { nonFieldLevelPermissionsGroups, fieldLevelPermissionsGroups } = getFilteredPermissionData(
    permissionType === 'feature-set'
      ? convertDualPermissions(mergeDate ?? [])
      : convertPermissions(newPermissions ?? []),
    'CustomerService.Customer.',
    allFieldValues
  );

  const filterNonFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(nonFieldLevelPermissionsGroups),
    SORTED_MODULE_PERMISSION
  );
  const filterFieldLevelPermission = handleSortPermissionNames(
    handleFilterPermissionList(fieldLevelPermissionsGroups),
    SORTED_FIELD_PERMISSION
  );
  const filteredData = Object.entries(filterNonFieldLevelPermission).reduce(
    (acc, [key, value]: any) => {
      const filteredItems = value.filter((item: any) =>
        item?.displayName?.toLowerCase().includes(manualSearch?.toLowerCase())
      );

      if (filteredItems.length > 0) {
        acc[key] = filteredItems;
      }
      return acc;
    },
    {} as typeof filterNonFieldLevelPermission
  );

  const filteredFieldLevelData = Object.entries(filterFieldLevelPermission).reduce(
    (acc, [key, value]: any) => {
      const filteredItems = value.filter((item: any) =>
        allFieldValues[item?.key]?.toLowerCase().includes(manualSearch?.toLowerCase())
      );
      if (filteredItems.length > 0) {
        acc[key] = filteredItems;
      }
      return acc;
    },
    {} as typeof filterFieldLevelPermission
  );
  const handleCheckedAll = (
    key: string,
    setFieldValue: any,
    dataList: any,
    wrapperName: string,
    isCheckedAll: boolean
  ): void => {
    const allFields = handleFilterPermissionList(dataList)[key];
    const filterAllDisabledData = allFields.filter(
      (item: any) => !(item.isDisabled as boolean) && item
    );

    if (isCheckedAll) {
      allFields.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, false)
      );
    } else {
      filterAllDisabledData.forEach((item: any) =>
        setFieldValue(`${wrapperName}[${String(item.key.replaceAll('.', '-') ?? '')}]`, true)
      );
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        name: '',
        description: '',
        permissions: {},
        fieldLevelPermissions: {},
        selectedModulePermissions: {},
        selectedFieldPermissions: {},
      }}
      //   validationSchema={}
      onSubmit={(values) => {
        // Collect allowed and denied permissions from selected rows only
        const allowedPermissions: string[] = [];
        const deniedPermissions: string[] = [];

        // Get module level permissions from selected rows
        Object.keys(values.selectedModulePermissions).forEach((rowKey) => {
          if (values.selectedModulePermissions[rowKey]) {
            // Row is selected, get all permissions for this row
            const rowPermissions = handleFilterPermissionList(nonFieldLevelPermissionsGroups)[
              rowKey
            ];
            if (rowPermissions) {
              rowPermissions.forEach((item: any) => {
                if (!item.isDisabled && item.key) {
                  const permKey = item.key.replaceAll('.', '-');
                  if (values.permissions[permKey]) {
                    // Permission is checked - add to allowed
                    allowedPermissions.push(item.key);
                  } else {
                    // Permission is unchecked - add to denied
                    deniedPermissions.push(item.key);
                  }
                }
              });
            }
          }
        });

        // Get field level permissions from selected rows
        Object.keys(values.selectedFieldPermissions).forEach((rowKey) => {
          if (values.selectedFieldPermissions[rowKey]) {
            // Row is selected, get all permissions for this row
            const rowPermissions = handleFilterPermissionList(fieldLevelPermissionsGroups)[rowKey];
            if (rowPermissions) {
              rowPermissions.forEach((item: any) => {
                if (!item.isDisabled && item.key) {
                  const permKey = item.key.replaceAll('.', '-');
                  if (values.fieldLevelPermissions[permKey]) {
                    // Permission is checked - add to allowed
                    allowedPermissions.push(item.key);
                  } else {
                    // Permission is unchecked - add to denied
                    deniedPermissions.push(item.key);
                  }
                }
              });
            }
          }
        });

        const payload = {
          featureSetIds: permissionType === 'feature-set' ? checkedValues : [],
          roleIds: permissionType === 'roles' ? checkedValues : [],
          allowed: allowedPermissions,
          denied: deniedPermissions,
        };

        const selectedFieldPermissionNames = Object.keys(values.selectedFieldPermissions || {})
          .filter((rowKey) => values.selectedFieldPermissions[rowKey])
          .map((rowKey) => {
            const rowPermissions = handleFilterPermissionList(fieldLevelPermissionsGroups)[rowKey];
            const labelKey = rowPermissions?.[0]?.key;
            return (labelKey && allFieldValues?.[labelKey]) || rowKey;
          });

        const selectedModulePermissionNames = Object.keys(values.selectedModulePermissions || {})
          .filter((rowKey) => values.selectedModulePermissions[rowKey])
          .map((rowKey) => {
            const rowPermissions = handleFilterPermissionList(nonFieldLevelPermissionsGroups)[
              rowKey
            ];
            const labelKey = rowPermissions?.[0]?.key;
            return (labelKey && allFieldValues?.[labelKey]) || rowKey;
          });

        const selectedPermissionNames = [
          ...selectedModulePermissionNames,
          ...selectedFieldPermissionNames,
        ];

        const buildPermissionRows = (rowKeys: string[], groups: any) => {
          return rowKeys.map((rowKey) => {
            const rowPermissions = handleFilterPermissionList(groups)[rowKey];
            const labelKey = rowPermissions?.[0]?.key;
            const displayName = (labelKey && allFieldValues?.[labelKey]) || rowKey;
            const permissions = (ROLES_PERMISSION_LIST as string[]).map((permText) => {
              const item = rowPermissions?.find((p: any) => p.text === permText);
              const applicable = !!(item?.key && !item.isDisabled);
              const allowed = item?.key ? allowedPermissions.includes(item.key) : false;
              return { text: permText, allowed, applicable };
            });
            return { displayName, permissions };
          });
        };

        const moduleRows = buildPermissionRows(
          Object.keys(values.selectedModulePermissions || {}).filter(
            (k) => values.selectedModulePermissions[k]
          ),
          nonFieldLevelPermissionsGroups
        );
        const fieldRows = buildPermissionRows(
          Object.keys(values.selectedFieldPermissions || {}).filter(
            (k) => values.selectedFieldPermissions[k]
          ),
          fieldLevelPermissionsGroups
        );
        const selectedPermissionRows = [...moduleRows, ...fieldRows];

        const modalData = {
          payload,
          selectedPermissionNames,
          selectedPermissionRows,
        };
        successModalDataRef.current = modalData;
        setSuccessCustomValue(modalData);
        openSuccessModal();
        // upsertPermission(payload);
      }}>
      {({ touched, handleChange, errors, values, setFieldValue }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            // loading={loading}
            size='xl'
            title={`Bulk Edit`}
            // valueEdited={_.isEqual(values, initialData.data)}
            id={id}
            children={
              <Form id='manageFeatureSetForm'>
                <ModalBody id='manageFeatureSetModalBody'>
                  {selectedRowData && (
                    <div className='flex flex-col gap-2 flex-start mb-8'>
                      <Heading
                        variant='h6'
                        title={
                          permissionType === 'feature-set'
                            ? 'Selected Feature Set'
                            : 'Selected Role'
                        }
                      />
                      <div className='flex flex-wrap gap-2 mt-4'>
                        {selectedRowData.map((item: any) => (
                          <Tag
                            variant='primary'
                            title={permissionType === 'feature-set' ? item.name : item.roleName}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <div id='searchSection'>
                    <Search
                      placeholder='Search Module & Field Level Permissions'
                      searchValue={manualSearch}
                      onSearch={(value: string) => {
                        setManualSearch(value);
                      }}
                      id='searchInput'
                    />
                  </div>

                  <FieldLevelPermissionsGroups id='moduleLevelPermissionsSection'>
                    <div className='grid grid-cols-12 gap-x-5 roleTitle'>
                      <div className='col-span-1'></div>
                      <div className='col-span-4'>
                        <Tag variant='info' title='Module Level Permission List' />
                      </div>
                      <div className='grid grid-cols-12 col-span-7'>
                        <Heading
                          variant='body-xs'
                          title='All'
                          className='col-span-2 roleSubTitle'
                        />
                        {ROLES_PERMISSION_LIST.map((col, i) => (
                          <Heading
                            key={`header-table-${i}`}
                            variant='body-xs'
                            title={col}
                            className='col-span-2 roleSubTitle'
                          />
                        ))}
                      </div>
                    </div>

                    {/* {Object.keys(filteredData)
                      .sort((a, b) => {
                        const keyA = filteredData[a][0]?.key || '';
                        const keyB = filteredData[b][0]?.key || '';

                        const valueA = allFieldValues[keyA] || '';
                        const valueB = allFieldValues[keyB] || '';

                        return valueA.localeCompare(valueB);
                      })
                      .map((data: string) => {
                        const allInfo = filteredData[data].filter(
                          (item: any) => !(item?.isDisabled as boolean) && item
                        );
                        const checkRoles = allInfo?.map(
                          (item: any) =>
                            !!(values?.permissions[item?.key.replaceAll('.', '-')] as boolean)
                        );
                        const labelKey = Boolean(allInfo) && allInfo?.length > 0 && allInfo[0].key; */}
                    {Object.keys(filteredData)
                      .sort((a, b) => {
                        const keyA = filteredData[a][0]?.key || '';
                        const keyB = filteredData[b][0]?.key || '';

                        const valueA = allFieldValues[keyA] || '';
                        const valueB = allFieldValues[keyB] || '';

                        return valueA.localeCompare(valueB);
                      })
                      .map((data: string) => {
                        const allInfo = filteredData[data].filter(
                          (item: any) => !(item.isDisabled as boolean) && item
                        );

                        const rolesInfo = filterNonFieldLevelPermission[data].filter(
                          (item: any) => !(item.isDisabled as boolean) && item
                        );

                        const checkRoles = rolesInfo.map(
                          (item: any) =>
                            !!(values.permissions[item.key.replaceAll('.', '-')] as boolean)
                        );

                        const labelKey = Boolean(allInfo) && allInfo.length > 0 && allInfo[0].key;

                        if (!allFieldValues[labelKey]) return;

                        return (
                          <ListWrapper className='grid items-center grid-cols-12 gap-x-5'>
                            <div className='justify-center col-span-1 CheckboxCenterInRoles mb-8'>
                              <CustomCheckBox
                                name={`selectedModulePermissions[${data}]`}
                                id={`selectedModulePermissions-${data}.enablePermission`}
                                onChange={(e: any) => {
                                  setFieldValue(
                                    `selectedModulePermissions[${data}]`,
                                    e.target.checked
                                  );
                                  // Clear all permissions for this row when unchecked
                                  if (!e.target.checked) {
                                    const rowPermissions = handleFilterPermissionList(
                                      nonFieldLevelPermissionsGroups
                                    )[data];
                                    if (rowPermissions) {
                                      rowPermissions.forEach((item: any) => {
                                        if (item.key) {
                                          setFieldValue(
                                            `permissions[${item.key.replaceAll('.', '-')}]`,
                                            false
                                          );
                                        }
                                      });
                                    }
                                  }
                                }}
                                errors={errors}
                                touched={touched}
                                arrValue={values?.selectedModulePermissions?.[data]}
                              />
                            </div>
                            <HeadingMain className='col-span-4'>
                              {(allFieldValues as boolean) ? allFieldValues[labelKey] : '-'}
                            </HeadingMain>
                            <CheckBoxFlex className='grid grid-cols-12 col-span-7'>
                              <div className='justify-center col-span-2 CheckboxCenterInRoles'>
                                <CustomCheckBox
                                  name={`nonFieldPermissions${data}All`}
                                  id={`nonFieldPermissions-${data}.All`}
                                  onChange={() => {
                                    handleCheckedAll(
                                      data,
                                      setFieldValue,
                                      nonFieldLevelPermissionsGroups,
                                      'permissions',
                                      checkRoles.every((v: boolean) => v)
                                    );
                                  }}
                                  errors={errors}
                                  touched={touched}
                                  checked={checkRoles.every((v: boolean) => v)}
                                  disabled={!values?.selectedModulePermissions?.[data]}
                                />
                              </div>
                              {ROLES_PERMISSION_LIST.map((item, index) => {
                                const itemData = handleFilterPermissionList(
                                  nonFieldLevelPermissionsGroups
                                )[`${data}`][index];
                                const isRowSelected = values?.selectedModulePermissions?.[data];
                                return (
                                  <div className='justify-center col-span-2 CheckboxCenterInRoles'>
                                    <CustomCheckBox
                                      name={`permissions[${String(
                                        itemData.key.replaceAll('.', '-') ?? ''
                                      )}]`}
                                      onChange={(e: any) => {
                                        if (
                                          !(itemData.key.includes('View') === true) &&
                                          e.target.checked === true
                                        ) {
                                          const newValue = itemData.key
                                            .replaceAll('.', '-')
                                            .split('-');

                                          const filterValue = newValue
                                            .filter(
                                              (item: string, index: number) =>
                                                index !== newValue.length - 1 && item
                                            )
                                            .join('-');
                                          setFieldValue(
                                            `permissions[${String(filterValue ?? '')}]`,
                                            true
                                          );
                                        }

                                        setFieldValue(
                                          `permissions[${String(
                                            itemData.key.replaceAll('.', '-') ?? ''
                                          )}]`,
                                          e.target.checked
                                        );
                                      }}
                                      errors={errors}
                                      touched={touched}
                                      id={`${itemData.key.replaceAll('.', '-')}.${item}`}
                                      arrValue={
                                        item === itemData.text &&
                                        values?.permissions[itemData.key.replaceAll('.', '-')]
                                      }
                                      disabled={
                                        !isRowSelected ||
                                        !(itemData.key.length as boolean) ||
                                        itemData.isDisabled
                                      }
                                    />
                                  </div>
                                );
                              })}
                            </CheckBoxFlex>
                          </ListWrapper>
                        );
                      })}
                  </FieldLevelPermissionsGroups>
                  <FieldLevelPermissionsGroups id='fieldLevelPermissionsSection'>
                    {Object.keys(filteredFieldLevelData).length > 0 && (
                      <div className='grid grid-cols-12 gap-x-5 roleTitle'>
                        <div className='col-span-1'></div>
                        <div className='col-span-4'>
                          <Tag variant='info' title='Field Level Permission' />
                        </div>
                      </div>
                    )}
                    {Object.keys(filteredFieldLevelData).map((data: string) => {
                      const allInfo = filteredFieldLevelData[data]?.filter(
                        (item: any) => !(item?.isDisabled as boolean) && item
                      );

                      const checkRoles = allInfo?.map(
                        (item: any) =>
                          !!(values?.fieldLevelPermissions[
                            item?.key?.replaceAll('.', '-')
                          ] as boolean)
                      );
                      const labelKey = Boolean(allInfo) && allInfo.length > 0 && allInfo[0].key;
                      return (
                        <ListWrapper
                          className='grid items-center grid-cols-12 gap-x-5'
                          id={`fieldLevelPermissionRow${data}`}>
                          <div className='justify-center col-span-1 CheckboxCenterInRoles mb-8'>
                            <CustomCheckBox
                              name={`selectedFieldPermissions[${data}]`}
                              id={`selectedFieldPermissions-${data}.enablePermission`}
                              onChange={(e: any) => {
                                setFieldValue(
                                  `selectedFieldPermissions[${data}]`,
                                  e.target.checked
                                );
                                // Clear all permissions for this row when unchecked
                                if (!e.target.checked) {
                                  const rowPermissions = handleFilterPermissionList(
                                    fieldLevelPermissionsGroups
                                  )[data];
                                  if (rowPermissions) {
                                    rowPermissions.forEach((item: any) => {
                                      if (item.key) {
                                        setFieldValue(
                                          `fieldLevelPermissions[${item.key.replaceAll('.', '-')}]`,
                                          false
                                        );
                                      }
                                    });
                                  }
                                }
                              }}
                              errors={errors}
                              touched={touched}
                              arrValue={values?.selectedFieldPermissions?.[data]}
                            />
                          </div>
                          <HeadingMain className='col-span-4'>
                            {(allFieldValues as boolean) ? allFieldValues[labelKey] : '-'}
                          </HeadingMain>
                          <CheckBoxFlex className='grid grid-cols-12 col-span-7'>
                            <div className='justify-center col-span-2 CheckboxCenterInRoles'>
                              <CustomCheckBox
                                name={`fieldLevelPermissions${data}All`}
                                id={`fieldLevelPermissions-${data}.All`}
                                onChange={() => {
                                  handleCheckedAll(
                                    data,
                                    setFieldValue,
                                    fieldLevelPermissionsGroups,
                                    'fieldLevelPermissions',
                                    checkRoles.every((v: boolean) => v)
                                  );
                                }}
                                errors={errors}
                                touched={touched}
                                checked={checkRoles.every((v: boolean) => v)}
                                disabled={!values?.selectedFieldPermissions?.[data]}
                              />
                            </div>
                            {ROLES_PERMISSION_LIST.map((item, index) => {
                              const itemData = handleFilterPermissionList(
                                fieldLevelPermissionsGroups
                              )[`${data}`][index];
                              const isRowSelected = values?.selectedFieldPermissions?.[data];
                              return (
                                <div className='justify-center col-span-2 CheckboxCenterInRoles'>
                                  <CustomCheckBox
                                    name={`fieldLevelPermissions[${String(
                                      itemData.key.replaceAll('.', '-') ?? ''
                                    )}]`}
                                    id={`${itemData.key.replaceAll('.', '-')}.${item}`}
                                    onChange={(e: any) => {
                                      if (
                                        !(itemData.key.includes('View') === true) &&
                                        e.target.checked === true
                                      ) {
                                        const newValue = itemData.key
                                          .replaceAll('.', '-')
                                          .split('-');

                                        const filterValue = newValue
                                          .filter(
                                            (item: string, index: number) =>
                                              index !== newValue.length - 1 && item
                                          )
                                          .join('-');
                                        setFieldValue(
                                          `fieldLevelPermissions[${String(filterValue ?? '')}]`,
                                          true
                                        );
                                      }

                                      setFieldValue(
                                        `fieldLevelPermissions[${String(
                                          itemData.key.replaceAll('.', '-') ?? ''
                                        )}]`,
                                        e.target.checked
                                      );
                                    }}
                                    errors={errors}
                                    touched={touched}
                                    arrValue={
                                      item === itemData.text &&
                                      values.fieldLevelPermissions[
                                        itemData.key.replaceAll('.', '-')
                                      ]
                                    }
                                    disabled={
                                      !isRowSelected ||
                                      !(itemData.key.length as boolean) ||
                                      itemData.isDisabled
                                    }
                                  />
                                </div>
                              );
                            })}
                          </CheckBoxFlex>
                        </ListWrapper>
                      );
                    })}
                  </FieldLevelPermissionsGroups>
                  <AnimationWrapper>
                    {isSuccessModalOpen && (
                      <AlertPopup
                        isShow={isSuccessModalOpen}
                        closeModal={() => {
                          closeSuccessModal();
                        }}
                        customTitle='Are you sure you want to update this permissions?'
                        subTitle={(() => {
                          const data = successCustomValue ?? successModalDataRef.current;
                          const rows = data?.selectedPermissionRows ?? [];
                          if (rows.length === 0) return null;
                          return (
                            <div>
                              <div className='grid grid-cols-12 gap-x-2 gap-y-0 mb-1 text-xs font-semibold text-gray-600 min-w-max'>
                                <div className='col-span-4'>
                                  <Heading variant='h6' title='Permission' />
                                </div>
                                <div className='col-span-1 flex justify-center items-center text-center'>
                                  <Heading variant='h6' title='All' />
                                </div>
                                {(ROLES_PERMISSION_LIST as string[]).map((col) => (
                                  <div
                                    key={col}
                                    className='col-span-1 flex justify-center items-center text-center'>
                                    <Heading variant='h6' title={col} />
                                  </div>
                                ))}
                              </div>
                              <PermissionConfirmScrollWrapper>
                                {rows.map((row, rowIndex) => {
                                  const applicablePerms = row.permissions.filter(
                                    (p) => p.applicable
                                  );
                                  const allAllowed =
                                    applicablePerms.length > 0 &&
                                    applicablePerms.every((p) => p.allowed);
                                  return (
                                    <div
                                      key={`${row.displayName}-${rowIndex}`}
                                      className='grid grid-cols-12 gap-x-2 gap-y-1 items-center py-2 border-b border-gray-200 last:border-b-0 min-w-max'>
                                      <div className='col-span-4 font-semibold text-gray-800 truncate'>
                                        {row.displayName}
                                      </div>
                                      <div className='col-span-1 flex justify-center'>
                                        <span
                                          className='inline-flex h-5 w-5 items-center justify-center rounded border flex-shrink-0'
                                          style={{
                                            backgroundColor: allAllowed ? '#22c55e' : '#f2f2f2',
                                            borderColor: allAllowed ? '#22c55e' : '#d2d2d2',
                                          }}>
                                          {allAllowed && (
                                            <span className='material-symbols-outlined text-white text-sm'>
                                              check
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                      {row.permissions.map((perm, permIndex) => (
                                        <div
                                          key={`${row.displayName}-${perm.text}-${permIndex}`}
                                          className='col-span-1 flex justify-center'>
                                          <span
                                            className='inline-flex h-5 w-5 items-center justify-center rounded border flex-shrink-0'
                                            style={{
                                              backgroundColor: perm.allowed ? '#22c55e' : '#f2f2f2',
                                              borderColor: perm.allowed ? '#22c55e' : '#d2d2d2',
                                            }}>
                                            {perm.allowed && (
                                              <span className='material-symbols-outlined text-white text-sm'>
                                                check
                                              </span>
                                            )}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })}
                              </PermissionConfirmScrollWrapper>
                            </div>
                          );
                        })()}
                        handleSubmit={() => {
                          const data = successCustomValue ?? successModalDataRef.current;
                          upsertPermission(data?.payload ?? data);
                        }}
                        loading={isLoading}
                      />
                    )}
                  </AnimationWrapper>
                </ModalBody>
                <ModalFooterComponent
                  id='manageFeatureSetModalFooter'
                  onCloseModal={closeModal}
                  urlName='feature-set'
                  modalStatus='Add'
                  valueChanged={false}
                  loading={isLoading}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default ManageFeatureSetModal;
